import { createContext, useContext, useState, useRef, useCallback } from 'react';

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [queue, setQueue] = useState([]);
  const audioRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  const playTrack = useCallback((track, trackList = []) => {
    if (track.external_url) {
      // Open external URL in new tab
      window.open(track.external_url, '_blank');
      return;
    }
    
    if (!track.audio_path) {
      console.warn('No audio available for this track');
      return;
    }

    setCurrentTrack(track);
    setQueue(trackList.filter(t => t.id !== track.id));
    setIsPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const playNext = useCallback(() => {
    if (queue.length > 0) {
      const nextTrack = queue[0];
      setCurrentTrack(nextTrack);
      setQueue(prev => prev.slice(1));
      setIsPlaying(true);
    }
  }, [queue]);

  const playPrevious = useCallback(() => {
    // Reset current track to beginning
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, []);

  const seekTo = useCallback((time) => {
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const getStreamUrl = useCallback((track) => {
    if (!track?.audio_path) return null;
    return `${API}/api/tracks/${track.id}/stream`;
  }, [API]);

  return (
    <PlayerContext.Provider value={{
      currentTrack,
      isPlaying,
      duration,
      currentTime,
      volume,
      queue,
      audioRef,
      playTrack,
      togglePlay,
      playNext,
      playPrevious,
      seekTo,
      setDuration,
      setCurrentTime,
      setVolume,
      setIsPlaying,
      getStreamUrl,
    }}>
      {children}
    </PlayerContext.Provider>
  );
};
